import { dia } from '@joint/core'
import type { MapDataExport } from '../../../../map-editor-entities/schema/exportSchema'
import type { MapObject } from '../../../../map-editor-entities/schema/types'
import { log, LogCategory } from '../../../../map-editor-shared/lib/logger'
import { convertElementToMapObject } from './elementConverter'
import { isRelationshipProperty } from './relationshipExtractor'
import {
  transformToCCTV,
  transformToWarningCCTV,
  transformToZone,
  transformToColumn,
  transformToParkingLocation,
  transformToSensor,
  transformToCharger,
  transformToGuideBoard,
  transformToElevator,
  transformToEmergencyBell,
  transformToArrow,
  transformToLine,
  transformToEntrance,
  transformToOnePassReader,
  transformToOccupancyLight,
  transformToLight,
  transformToCustomObject
} from './transformers'

/**
 * Route object to appropriate category based on type or layer.
 * Delegates to centralized layerUtils. Re-exported for backward compatibility.
 */
import { routeLayerToCategory } from '../../../../map-editor-shared/lib/layerUtils'
export const routeObjectByType = routeLayerToCategory

/**
 * Build MapDataExport from graph with type-specific arrays
 */
export function buildMapData(
  graph: dia.Graph,
  metadata: {
    lotName: string
    floorName: string
    floorOrder?: number
    author?: string
    description?: string
  },
  layerFilter?: Set<string>
): MapDataExport {
  let elements = graph.getElements()

  // If layerFilter is provided, only include elements whose data.layer is in the filter
  if (layerFilter !== undefined) {
    if (layerFilter.size === 0) {
      elements = []
    } else {
      elements = elements.filter(el => {
        const layer = el.get('data')?.layer
        return layer && layerFilter.has(layer)
      })
    }
  }

  // Convert JointJS elements to MapObjects, assign sequential int ids (1..N),
  // and build the relationship remap table. The router keys objects by *name*,
  // so relationships must reference name — remap jointId → obj.name, not the
  // new int id. The int id is export-only metadata.
  const idRemapTable = new Map<string, string>()
  const objects = elements.map((element, index) => {
    const obj = convertElementToMapObject(element)
    const jointId = element.id as string
    idRemapTable.set(jointId, obj.name)
    obj.id = index + 1
    return obj
  })

  // Remap relationship IDs from JointJS element IDs to exported object IDs
  if (idRemapTable.size > 0) {
    for (const object of objects) {
      if (!object.properties) continue
      for (const [key, value] of Object.entries(object.properties)) {
        if (!isRelationshipProperty(key)) continue
        // 복수 관계(…Ids)가 문자열로 들어온 경우(속성 시트에서 직접 타이핑) →
        // 쉼표 구분 배열로 정규화. 빈 문자열은 빈 배열.
        if (/Ids$|Refs$/.test(key) && typeof value === 'string') {
          object.properties[key] = value
            .split(',')
            .map(v => v.trim())
            .filter(Boolean)
            .map(id => idRemapTable.get(id) ?? id)
          continue
        }
        if (Array.isArray(value)) {
          object.properties[key] = value.map(id =>
            typeof id === 'string' ? (idRemapTable.get(id) ?? id) : id
          )
        } else if (typeof value === 'string' && idRemapTable.has(value)) {
          object.properties[key] = idRemapTable.get(value)!
        }
      }
    }
  }

  // Group objects by type. carChargers/chargers 두 카테고리는 backend DTO 가
  // 'carChargers' 한 필드만 받으므로 동일 버킷으로 합친다.
  // cctv vs warningCctv 는 router DTO 의 CctvGroup.lightCctvs / warningCctvs
  // 두 필드로 분리되므로 각자 별도 버킷.
  const grouped = {
    cctvs: [] as MapObject[],
    warningCctvs: [] as MapObject[],
    zones: [] as MapObject[],
    columns: [] as MapObject[],
    parkingLocations: [] as MapObject[],
    sensors: [] as MapObject[],
    guideBoards: [] as MapObject[],
    elevators: [] as MapObject[],
    emergencyBells: [] as MapObject[],
    arrows: [] as MapObject[],
    outLines: [] as MapObject[],
    innerLines: [] as MapObject[],
    entrances: [] as MapObject[],
    onePassReaders: [] as MapObject[],
    occupancyLights: [] as MapObject[],
    lights: [] as MapObject[],
    carChargers: [] as MapObject[],
  }

  // 표준 카테고리에 매칭되지 않는 사용자 정의 레이어 객체는 레이어 이름별로
  // 묶는다. export 시 그 레이어 이름이 곧 최상위 키가 되며(예: 'dongsk'),
  // 새 레이어를 만들수록 키가 늘어나고 같은 레이어 객체는 한 배열에 누적된다.
  const customGroupsByLayer = new Map<string, MapObject[]>()

  // Route objects to appropriate groups
  for (const object of objects) {
    // Try layer field first, then fallback to type if layer doesn't match
    let category = 'unknown'

    if (object.layer) {
      category = routeObjectByType(object.layer)
    }

    // If layer didn't match or wasn't provided, try type field
    if (category === 'unknown' && object.type) {
      category = routeObjectByType(object.type)
    }

    if (category === 'cctv') grouped.cctvs.push(object)
    else if (category === 'warningCctv') grouped.warningCctvs.push(object)
    else if (category === 'zone') grouped.zones.push(object)
    else if (category === 'column') grouped.columns.push(object)
    else if (category === 'parkingLocation') grouped.parkingLocations.push(object)
    else if (category === 'sensor') grouped.sensors.push(object)
    else if (category === 'charger' || category === 'carCharger') grouped.carChargers.push(object)
    else if (category === 'guideBoard') grouped.guideBoards.push(object)
    else if (category === 'elevator') grouped.elevators.push(object)
    else if (category === 'emergencyBell') grouped.emergencyBells.push(object)
    else if (category === 'arrow') grouped.arrows.push(object)
    else if (category === 'outLine') grouped.outLines.push(object)
    else if (category === 'innerLine') grouped.innerLines.push(object)
    else if (category === 'entrance') grouped.entrances.push(object)
    else if (category === 'onePassReader') grouped.onePassReaders.push(object)
    else if (category === 'occupancyLight') grouped.occupancyLights.push(object)
    else if (category === 'light') grouped.lights.push(object)
    else {
      // 표준 카테고리 미매칭 객체(사용자 정의 레이어 'dongs' 등)는 누락하지 않고
      // 레이어 이름(없으면 type)별 버킷에 보존 → export 시 그 이름이 최상위 키가 된다.
      const customKey = object.layer || object.type || 'customObjects'
      log.warn(LogCategory.GENERAL, `Unmapped object → layer bucket "${customKey}": ${object.layer || object.type}`, object)
      const bucket = customGroupsByLayer.get(customKey)
      if (bucket) bucket.push(object)
      else customGroupsByLayer.set(customKey, [object])
    }
  }

  // Transform each group to export format
  // Router API requires all arrays to be present (even if empty)
  const result: MapDataExport = {
    cctvs: {
      lightCctvs: grouped.cctvs.map(transformToCCTV),
      warningCctvs: grouped.warningCctvs.map(transformToWarningCCTV),
    },
    zones: grouped.zones.map(transformToZone),
    columns: grouped.columns.map(transformToColumn),
    parkingLocations: grouped.parkingLocations.map(transformToParkingLocation),
    sensors: grouped.sensors.map(transformToSensor),
    chargers: [],
    guideBoards: grouped.guideBoards.map(transformToGuideBoard),
    elevators: grouped.elevators.map(transformToElevator),
    emergencyBells: grouped.emergencyBells.map(transformToEmergencyBell),
    arrows: grouped.arrows.map(transformToArrow),
    outLines: grouped.outLines.map(transformToLine),
    innerLines: grouped.innerLines.map(transformToLine),
    entrances: grouped.entrances.map(transformToEntrance),
    onePassReaders: grouped.onePassReaders.map(transformToOnePassReader),
    occupancyLights: grouped.occupancyLights.map(transformToOccupancyLight),
    lights: grouped.lights.map(transformToLight),
    carChargers: grouped.carChargers.map(transformToCharger),
  }

  // 사용자 정의 레이어 객체를 레이어 이름 키로 최상위에 첨부한다.
  // 커스텀 레이어가 없는 맵은 기존 export 출력 형태를 그대로 유지한다.
  // 표준 export 키(zones/cctvs/...)와 이름이 겹치면 데이터 유실을 막기 위해
  // '{layer}_custom' 으로 회피한다.
  if (customGroupsByLayer.size > 0) {
    const target = result as Record<string, unknown>
    const reservedKeys = new Set(Object.keys(result))
    for (const [layer, layerObjects] of customGroupsByLayer) {
      const key = reservedKeys.has(layer) ? `${layer}_custom` : layer
      if (reservedKeys.has(layer)) {
        log.warn(LogCategory.GENERAL, `Custom layer "${layer}" collides with reserved export key; using "${key}"`)
      }
      target[key] = layerObjects.map(transformToCustomObject)
    }
  }

  return result
}
